import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo, XMark } from "./Logo";
import { useLaunchModal } from "./LaunchModal";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
    { label: "Markets", id: "markets", tid: "nav-link-markets" },
    { label: "Trade", id: "why-tavonx", tid: "nav-link-trade" },
    { label: "Earn", id: "tvx", tid: "nav-link-earn" },
    { label: "Ecosystem", id: "ecosystem", tid: "nav-link-ecosystem" },
    { label: "About", id: "global", tid: "nav-link-about" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { open: openModal } = useLaunchModal();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const go = (id) => (e) => {
        setOpen(false);
        scrollToId(id)(e);
    };
    const launch = (v) => (e) => {
        e.preventDefault();
        setOpen(false);
        openModal(v);
    };

    return (
        <>
            <header
                data-testid="navbar"
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled || open
                        ? "border-b border-white/[0.06] bg-base/80 backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8 lg:px-12">
                    <Logo onClick={go("hero")} />

                    <nav className="hidden items-center gap-9 lg:flex">
                        {LINKS.map((l) => (
                            <a
                                key={l.id}
                                href={`#${l.id}`}
                                onClick={go(l.id)}
                                data-testid={l.tid}
                                className="group relative font-grotesk text-[13px] font-medium uppercase tracking-[0.18em] text-slate-300 transition-colors hover:text-white"
                            >
                                {l.label}
                                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-cyber transition-all duration-300 group-hover:w-full" />
                            </a>
                        ))}
                    </nav>

                    <div className="hidden items-center gap-2.5 lg:flex">
                        <a
                            href="#"
                            onClick={launch("login")}
                            data-testid="nav-login-btn"
                            className="group relative px-4 py-2 font-grotesk text-[13px] font-medium uppercase tracking-[0.16em] text-slate-300 transition-colors duration-200 hover:text-white"
                        >
                            Log In
                            <span className="absolute bottom-0.5 left-4 h-px w-[calc(100%-2rem)] origin-left scale-x-0 bg-cyber transition-transform duration-200 group-hover:scale-x-100" />
                        </a>
                        <a
                            href="#"
                            onClick={launch("signup")}
                            data-testid="nav-signup-btn"
                            className="rounded border border-white/15 px-4 py-2 font-grotesk text-[13px] font-medium uppercase tracking-[0.16em] text-white transition-all duration-200 hover:-translate-y-0.5 hover:border-cyber/60 hover:shadow-[0_0_18px_rgba(0,209,255,0.25)]"
                        >
                            Sign Up
                        </a>
                        <a
                            href="#"
                            onClick={launch("trading")}
                            data-testid="nav-cta-start-trading"
                            className="rounded bg-gradient-to-r from-electric to-cyber px-5 py-2.5 font-grotesk text-[13px] font-semibold uppercase tracking-[0.16em] text-void transition-all duration-200 hover:shadow-[0_0_30px_rgba(0,209,255,0.45)] hover:brightness-110"
                        >
                            Start Trading
                        </a>
                    </div>

                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="p-2 text-white lg:hidden"
                        aria-label="Toggle menu"
                        aria-expanded={open}
                    >
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-drawer"
                        initial={{ opacity: 0, y: -14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -14 }}
                        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed inset-0 z-40 bg-void/95 backdrop-blur-2xl lg:hidden"
                    >
                        <div className="flex h-full flex-col justify-center overflow-y-auto px-8 pb-10 pt-24">
                            {LINKS.map((l, i) => (
                                <motion.a
                                    key={l.id}
                                    href={`#${l.id}`}
                                    onClick={go(l.id)}
                                    initial={{ opacity: 0, x: -22 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.06 }}
                                    className="flex items-baseline border-b border-white/[0.06] py-5 font-grotesk text-3xl font-bold uppercase tracking-wide text-white"
                                >
                                    <span className="mr-5 font-mono text-xs tracking-[0.3em] text-cyber">
                                        0{i + 1}
                                    </span>
                                    {l.label}
                                </motion.a>
                            ))}
                            <div className="mt-10 grid gap-3">
                                <a
                                    href="#"
                                    onClick={launch("login")}
                                    data-testid="nav-mobile-login"
                                    className="rounded border border-white/15 py-3.5 text-center font-grotesk text-sm font-medium uppercase tracking-[0.18em] text-white"
                                >
                                    Log In
                                </a>
                                <a
                                    href="#"
                                    onClick={launch("signup")}
                                    data-testid="nav-mobile-signup"
                                    className="rounded border border-white/15 py-3.5 text-center font-grotesk text-sm font-medium uppercase tracking-[0.18em] text-white"
                                >
                                    Sign Up
                                </a>
                                <a
                                    href="#"
                                    onClick={launch("trading")}
                                    data-testid="nav-mobile-cta"
                                    className="flex items-center justify-center gap-2 rounded bg-gradient-to-r from-electric to-cyber py-3.5 font-grotesk text-sm font-semibold uppercase tracking-[0.18em] text-void"
                                >
                                    <XMark className="h-4 w-auto" withNode={false} />
                                    Start Trading
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
